import { useEffect, useMemo, useState } from 'react';
import { InfoCard } from './common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, ComposedChart, Area,
} from 'recharts';
import { RefreshCcw, TrendingUp, Sprout, IndianRupee, Droplets, Satellite, FileDown } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ICRISAT_GREEN = '#1a6b3c';
const ACCENT_GOLD = '#eab308';
const ACCENT_RUST = '#c2410c';
const ACCENT_SKY = '#0e7490';

const Analytics = ({ farmer, farm }) => {
  const [allFarmers, setAllFarmers] = useState([]);
  const [allFarms, setAllFarms] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.listFarmers().then(setAllFarmers).catch(() => {});
    api.listFarms().then(setAllFarms).catch(() => {});
    if (farm?.id) {
      setLoading(true);
      api.farmAnalytics(farm.id)
        .then(setAnalytics)
        .catch(() => setAnalytics(null))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => { load(); }, [farm?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {farm && (
        <InfoCard
          title={`Advisory history · ${farm.name}`}
          subtitle="Every advisory you run against this farm is auto-saved. Trends below rebuild across every season and year."
        >
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="text-sm text-icrisat-earth">
              <span className="font-medium text-icrisat-green">{analytics?.total_runs ?? 0}</span> total advisory runs
              {analytics && Object.entries(analytics.counts || {}).length > 0 && (
                <span className="ml-2 text-xs">
                  ({Object.entries(analytics.counts).map(([k, v]) => `${k}=${v}`).join(', ')})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="refresh-analytics-btn"
                className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
              >
                <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                asChild variant="default" size="sm"
                data-testid="download-bundle-pdf-btn"
                className="bg-icrisat-green hover:bg-icrisat-green/90 text-white"
              >
                <a href={`${BACKEND}/api/farms/${farm.id}/advisory-bundle.pdf`} target="_blank" rel="noreferrer">
                  <FileDown className="h-4 w-4 mr-1" />
                  Download PDF bundle
                </a>
              </Button>
            </div>
          </div>

          {!analytics && !loading && (
            <NoData>Run any advisory from the tabs above (Suitability, AI Weather Advisory, DNDC, Weather, Market Mandi, …) to populate this dashboard.</NoData>
          )}

          {analytics && analytics.total_runs === 0 && (
            <NoData>No history yet — run any advisory from another tab to populate this dashboard.</NoData>
          )}

          {analytics && analytics.total_runs > 0 && <TrendGrid trends={analytics.trends} />}
        </InfoCard>
      )}

      {farm && <RecentRunsTable farm={farm} />}

      <InfoCard title="Farm Registry" subtitle="Every farmer + farm ever registered in AgriAgent.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Registered farmers" value={allFarmers.length} />
          <Stat label="Registered farms" value={allFarms.length} />
          <Stat label="Active farmer" value={farmer?.name || '—'} />
          <Stat label="Districts covered" value={new Set(allFarmers.map(f => f.district).filter(Boolean)).size} />
        </div>

        <h4 className="font-medium text-icrisat-green mt-4">Farmers</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Village</TableHead>
              <TableHead>Mandal</TableHead><TableHead>District</TableHead><TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allFarmers.map(f => (
              <TableRow key={f.id}>
                <TableCell className="font-medium">{f.name}</TableCell>
                <TableCell>{f.phone || '—'}</TableCell>
                <TableCell>{f.village || '—'}</TableCell>
                <TableCell>{f.mandal || '—'}</TableCell>
                <TableCell>{f.district || '—'}</TableCell>
                <TableCell className="text-xs">{f.created_at?.split('T')[0]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <h4 className="font-medium text-icrisat-green mt-6">Farms</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Lat, Lon</TableHead><TableHead>Area (ha)</TableHead>
              <TableHead>Soil</TableHead><TableHead>Irrigation</TableHead><TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allFarms.map(f => (
              <TableRow key={f.id}>
                <TableCell className="font-medium">{f.name}</TableCell>
                <TableCell>{f.lat.toFixed(4)}, {f.lon.toFixed(4)}</TableCell>
                <TableCell>{f.area_ha || '—'}</TableCell>
                <TableCell>{f.soil_type || '—'}</TableCell>
                <TableCell className="capitalize">{f.irrigation_availability?.replace('_', ' ')}</TableCell>
                <TableCell className="text-xs">{f.created_at?.split('T')[0]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </InfoCard>
    </>
  );
};

// ---------------------------------------------------------------------------
// Trend charts
// ---------------------------------------------------------------------------

const TrendGrid = ({ trends }) => (
  <div className="grid gap-4 lg:grid-cols-2 stagger">
    <YieldEconomicsCard trends={trends} />
    <SowingIsatCard trends={trends} />
    <MandiCard trends={trends} />
    <SuitabilityCard trends={trends} />
    <WeatherCard trends={trends} />
    <IrrigationCard trends={trends} />
    <LandCoverCard trends={trends} />
  </div>
);

const YieldEconomicsCard = ({ trends }) => {
  const data = (trends.dndc || []).map(r => ({
    date: r.date,
    yield: r.yield_kg_ha,
    net: r.net_return_rs_ha,
    ghg: r.total_CO2eq_kg_ha,
    label: r.crop || '',
  }));
  return (
    <ChartCard title="DNDC · Yield & Net Return" icon={<Sprout className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: 'Yield (kg/ha)', angle: -90, position: 'insideLeft', fill: ICRISAT_GREEN, fontSize: 11 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} label={{ value: 'Net return ₹/ha', angle: 90, position: 'insideRight', fill: ACCENT_GOLD, fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="left" dataKey="yield" fill={ICRISAT_GREEN} name="Yield kg/ha" />
          <Line yAxisId="right" type="monotone" dataKey="net" stroke={ACCENT_GOLD} strokeWidth={2} name="Net ₹/ha" dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const SowingIsatCard = ({ trends }) => {
  const data = (trends.sowing_isat || []).map(r => ({
    date: r.date,
    sow: r.sow_count,
    hold: r.hold_count,
    smap: r.smap_surface,
  }));
  return (
    <ChartCard title="AI Weather Advisory · SOW vs HOLD" icon={<TrendingUp className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} label={{ value: '# of crops', angle: -90, position: 'insideLeft', fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="sow" fill={ICRISAT_GREEN} name="SOW decisions" stackId="a" />
          <Bar dataKey="hold" fill={ACCENT_RUST} name="HOLD decisions" stackId="a" />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const MandiCard = ({ trends }) => {
  const data = (trends.market_mandi || []).map(r => ({
    date: r.date,
    net: r.best_net_price,
    label: r.best_market,
  }));
  return (
    <ChartCard title="Mandi · Best Net Price ₹/quintal" icon={<IndianRupee className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Line type="monotone" dataKey="net" stroke={ACCENT_GOLD} strokeWidth={2} dot={{ r: 4, fill: ACCENT_GOLD }} name="Best net ₹/q" />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const SuitabilityCard = ({ trends }) => {
  const data = (trends.suitability || []).map(r => ({
    date: r.date, top: r.top_score, hs: r.num_highly_suitable, crop: r.top_crop,
  }));
  return (
    <ChartCard title="Suitability · Top score & Highly-Suitable count" icon={<Sprout className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={[0, 100]} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area yAxisId="left" type="monotone" dataKey="top" fill={ICRISAT_GREEN} stroke={ICRISAT_GREEN} fillOpacity={0.25} name="Top score" />
          <Bar yAxisId="right" dataKey="hs" fill={ACCENT_SKY} name="# Highly Suitable" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const WeatherCard = ({ trends }) => {
  const data = (trends.weather || []).map(r => ({ date: r.date, gdd: r.cum_gdd, past: r.past_rain_mm, fc: r.forecast_rain_mm }));
  return (
    <ChartCard title="Weather · Cumulative GDD & Rain" icon={<Droplets className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line yAxisId="left" type="monotone" dataKey="gdd" stroke={ICRISAT_GREEN} strokeWidth={2} name="Cum GDD" />
          <Bar yAxisId="right" dataKey="past" fill={ACCENT_SKY} name="Past rain mm" />
          <Bar yAxisId="right" dataKey="fc" fill={ACCENT_GOLD} name="Forecast rain mm" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const IrrigationCard = ({ trends }) => {
  const data = (trends.irrigation || []).map(r => ({
    date: r.date, etc: r.total_etc_mm_7day, irr: r.total_irrigation_mm_7day,
  }));
  return (
    <ChartCard title="Irrigation · 7-day ETc vs Irrigation" icon={<Droplets className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="etc" fill={ACCENT_RUST} name="ETc mm" />
          <Bar dataKey="irr" fill={ACCENT_SKY} name="Irrigation mm" />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const LandCoverCard = ({ trends }) => {
  const data = (trends.land_cover || []).map(r => ({
    date: r.date, area: r.crop_area_pct, ok: r.decision === 'PROCEED' ? 1 : 0, dom: r.dominant,
  }));
  return (
    <ChartCard title="Land-cover · Crop-area % over checks" icon={<Satellite className="h-4 w-4" />} empty={data.length === 0}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="area" stroke={ICRISAT_GREEN} fill={ICRISAT_GREEN} fillOpacity={0.25} name="Crop area %" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const RecentRunsTable = ({ farm }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (farm?.id) api.history(farm.id).then(d => setRows(d.runs || [])).catch(() => setRows([]));
  }, [farm?.id]);

  if (!rows.length) return null;

  return (
    <InfoCard title={`Recent runs · ${farm.name}`} subtitle="Latest 20 advisories with the metrics captured for the trend charts.">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Advisory</TableHead>
              <TableHead>Crop</TableHead>
              <TableHead>Season</TableHead>
              <TableHead>Key metrics</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 20).map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-xs whitespace-nowrap">{r.timestamp?.slice(0, 16).replace('T', ' ')}</TableCell>
                <TableCell><Badge className="bg-icrisat-green-soft text-icrisat-green">{r.advisory_type}</Badge></TableCell>
                <TableCell className="text-xs">{r.crop_key || '—'}</TableCell>
                <TableCell className="text-xs">{r.season || '—'}</TableCell>
                <TableCell className="text-xs font-mono max-w-[420px] truncate" title={JSON.stringify(r.metrics)}>
                  {Object.entries(r.metrics || {}).map(([k, v]) => `${k}: ${format(v)}`).join(' · ') || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </InfoCard>
  );
};

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

const ChartCard = ({ title, icon, empty, children }) => (
  <div className="rounded-xl bg-white border border-icrisat-green/20 p-4">
    <div className="flex items-center gap-2 mb-2 text-icrisat-green">
      {icon}<span className="font-medium">{title}</span>
    </div>
    {empty ? (
      <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground italic">
        No runs yet
      </div>
    ) : (
      <div className="h-[220px]">{children}</div>
    )}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-4">
    <div className="text-[10px] uppercase tracking-wider text-icrisat-earth">{label}</div>
    <div className="font-display text-2xl text-icrisat-green">{value}</div>
  </div>
);

const NoData = ({ children }) => (
  <div className="p-6 border border-dashed border-icrisat-green/30 rounded-lg text-center text-sm text-muted-foreground bg-white/50">
    {children}
  </div>
);

const format = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? v.toString() : v.toFixed(2);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export default Analytics;
